import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, BadgeCheck, FlaskConical } from "lucide-react";
import NebulaHero from "@/components/NebulaHero";
import ProcessTimeline from "@/components/ProcessTimeline";
import { StatCounter } from "@/components/StatCounter";

export default function Home() {
  return (
    <div>
      {/* CINEMATIC HERO */}
      <NebulaHero />

      {/* ANIMATED STAT BAR */}
      <section className="relative border-y border-[var(--line)] bg-[var(--paper-2)] overflow-hidden">
        <div className="absolute inset-0 starfield opacity-30 pointer-events-none" />
        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Metric label="Avg. HPLC Purity"  value={<><StatCounter value={99.4} decimals={1} />%</>} />
          <Metric label="Active Compounds"  value={<><StatCounter value={12} />+</>} />
          <Metric label="Batches Verified"  value={<><StatCounter value={12} /></>} />
          <Metric label="On-Time Dispatch"    value={<><StatCounter value={100} />%</>} />
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-[var(--line)] bg-[var(--paper-2)]">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Trust icon={BadgeCheck} k="Independent third-party" v="Koveralabs analytical testing" />
          <Trust icon={Truck} k="1-2 day processing" v="USPS Priority · tracked dispatch" />
          <Trust icon={ShieldCheck} k="Per-batch COA" v="HPLC + Mass Spec verified" />
          <Trust icon={FlaskConical} k="Research use only" v="21+ verified buyers" />
        </div>
      </section>

      {/* RESEARCH STANDARD — non-product compliance section */}
      <section className="relative border-b border-[var(--line)] overflow-hidden">
        <div className="absolute inset-0 starfield opacity-20 pointer-events-none" />
        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow mb-3">The Standard</div>
            <h2 className="font-display text-4xl md:text-5xl font-semibold leading-[1.05] tracking-tight">
              Synthesize the unknown.<br/>
              <span className="text-[var(--terra)]">Every batch is verified.</span>
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[var(--ink-soft)] max-w-md">
              We supply laboratories and qualified researchers only. Every lot is independently assayed by Koveralabs — HPLC purity, mass spec confirmation, and net-content gravimetry — before a single vial leaves the facility. No exceptions. No marketing chemistry.
            </p>
            <Link to="/verify" className="mt-7 inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.18em] text-[var(--terra)] hover:text-[var(--terra-2)]" data-testid="home-verify-cta">
              Verify a batch <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-4">
            <Pillar k="HPLC" v="Reverse-phase purity assay across every active lot." />
            <Pillar k="Mass Spec" v="ESI-MS confirms molecular identity before release." />
            <Pillar k="Net Content" v="Gravimetric assay verifies labeled mass per vial." />
            <Pillar k="Chain of Custody" v="Lot-to-COA traceability, public batch search." />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS / PROCESS TIMELINE */}
      <ProcessTimeline />

      {/* CATEGORY SHORTCUTS */}
      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { slug: "metabolic-research-peptides", name: "Metabolic Research Peptides", img: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=600&q=80" },
            { slug: "research-peptides", name: "Research Peptides", img: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=600&q=80" },
            { slug: "lab-supplies", name: "Lab Supplies", img: "https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=600&q=80" },
          ].map((c) => (
            <Link key={c.slug} to={`/shop?cat=${c.slug}`} className="group relative aspect-[4/5] overflow-hidden rounded-xl bg-[var(--paper-2)] border border-[var(--line)] hover:border-[var(--terra)] transition-colors" data-testid={`cat-card-${c.slug}`}>
              <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-50 transition-transform duration-500 group-hover:scale-[1.05] group-hover:opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--paper)] via-[var(--paper)]/60 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--terra)]/0 via-transparent to-[var(--magenta)]/15 mix-blend-screen" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-display text-2xl font-semibold text-[var(--ink)]">{c.name}</div>
                <div className="mt-1 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.16em] text-[var(--terra)]">Browse <ArrowRight className="h-3 w-3" /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

const Metric = ({ label, value }) => (
  <div>
    <div className="font-display text-4xl md:text-5xl font-semibold leading-none">{value}</div>
    <div className="mt-2 text-[10.5px] font-mono uppercase tracking-[0.18em] text-[var(--ink-muted)]">{label}</div>
  </div>
);
const Trust = ({ icon: Icon, k, v }) => (
  <div className="flex items-start gap-3">
    <Icon className="h-5 w-5 text-[var(--terra)] shrink-0 mt-0.5" strokeWidth={1.6} />
    <div>
      <div className="text-sm font-medium text-[var(--ink)]">{k}</div>
      <div className="text-xs text-[var(--ink-muted)]">{v}</div>
    </div>
  </div>
);
const Pillar = ({ k, v }) => (
  <div className="border border-[var(--line)] bg-[var(--paper-2)]/40 backdrop-blur-sm rounded-md p-5 hover:border-[var(--terra)] transition-colors">
    <div className="font-display text-xl font-semibold text-[var(--ink)]">{k}</div>
    <div className="mt-2 text-xs leading-relaxed text-[var(--ink-muted)]">{v}</div>
  </div>
);
